// # Customer Analysis Calculator
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import { nationalityData } from '../../../../../../utils/nationality_data';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ZAxis,
  ComposedChart,
  Area
} from 'recharts';
import { 
  FaUsers, 
  FaUserPlus, 
  FaUserCheck, 
  FaMoneyBillWave, 
  FaGlobe, 
  FaShoppingCart,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import './customer_analysis.css';

const CustomerAnalysis = ({ dateRange, onDataProcessed }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState({
    totalCustomers: 0,
    newCustomers: 0,
    returningCustomers: 0,
    averageSpend: 0,
    customerLifetimeValue: 0,
    customersByNationality: [],
    customersByRegion: [],
    customersByProduct: [],
    spendingOverTime: [],
    customerSegments: [],
    retentionRate: 0,
    customerAcquisitionCost: 0,
    customerDetails: [],
    customerGrowthRate: 0
  });
  
  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // # Add state for nationality view tab
  const [nationalityView, setNationalityView] = useState('country'); // 'country' or 'region'
  
  // # Add state for search functionality
  const [searchTerm, setSearchTerm] = useState('');
  
  // # Fetch and process customer data
  useEffect(() => {
    const fetchCustomerData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Extract all payment records
        let allPayments = [];
        
        // # Process each document
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions
              if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
                // # Extract payment date from metadata
                const paymentDate = new Date(paymentData.metadata?.created_at || '');
                
                // # Apply date range filter if provided
                if (dateRange) {
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  
                  // # Set hours for proper date comparison
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(23, 59, 59, 999);
                  
                  // # Skip if outside date range
                  if (paymentDate < startDate || paymentDate > endDate) {
                    return;
                  }
                }
                
                // # Add payment to collection with all necessary fields - directly use stored values
                allPayments.push({
                  id: paymentId,
                  doc_id: docId,
                  timestamp: paymentDate,
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  duration: paymentData.pay?.duration || '',
                  nationality: paymentData.user?.nationality || 'Unknown',
                  email: paymentData.user?.email || '',
                  name: paymentData.user?.name || '',
                  userId: paymentData.user?.userId || '',
                  phone: paymentData.user?.phone || '',
                  currency: paymentData.pay?.currency || '',
                  currency_symbol: paymentData.pay?.currency_symbol || '',
                  mentor: paymentData.mentor?.mentor_name || '',
                  mentor_id: paymentData.mentor?.uid || '',
                  tutor: paymentData.tutor?.tutor_name || '',
                  tutor_id: paymentData.tutor?.uid || '',
                  budget_dvd_amount: paymentData.budget_dvd?.egp_amount || 0,
                  budget_dvd_percent: paymentData.budget_dvd?.percentage_of_total || 0,
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        });
        
        // # Process customer data with the payments
        const processedData = processCustomerData(allPayments);
        
        // # Call the onDataProcessed callback if provided
        if (onDataProcessed) {
          onDataProcessed(allPayments);
        }
        
        // # Update customerData with processed data
        setCustomerData(processedData);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setError('Failed to load customer data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [dateRange, onDataProcessed]);
  
  // # Process customer data for charts - simplified to use pre-calculated values
  const processCustomerData = (payments) => {
    // # Initialize data structures
    const customerData = {
      totalCustomers: 0,
      newCustomers: 0,
      returningCustomers: 0,
      averageSpend: 0,
      customerLifetimeValue: 0,
      customersByNationality: [],
      customersByRegion: [],
      customersByProduct: [],
      spendingOverTime: [],
      customerSegments: [],
      retentionRate: 0,
      customerAcquisitionCost: 0,
      customerDetails: [],
      customerGrowthRate: 0
    };
    
    // # Group payments by customer (email or userId)
    const customerPayments = {};
    
    // # Sort payments by timestamp for time-based analysis
    payments.sort((a, b) => a.timestamp - b.timestamp);
    
    payments.forEach(payment => {
      const customerId = payment.email || payment.userId || 'unknown';
      
      if (!customerPayments[customerId]) {
        customerPayments[customerId] = {
          id: customerId,
          name: payment.name,
          email: payment.email,
          phone: payment.phone,
          nationality: payment.nationality,
          payments: [],
          totalSpend: 0,
          firstPurchase: payment.timestamp,
          lastPurchase: payment.timestamp,
          products: new Set(),
          mentors: new Set()
        };
      }
      
      // # Update customer data
      const customer = customerPayments[customerId];
      customer.payments.push(payment);
      customer.totalSpend += payment.egp_total;
      customer.products.add(payment.product);
      
      // # Track mentors if applicable
      if (payment.mentor) {
        customer.mentors.add(payment.mentor);
      }
      
      // # Update first and last purchase dates
      if (payment.timestamp < customer.firstPurchase) {
        customer.firstPurchase = payment.timestamp;
      }
      if (payment.timestamp > customer.lastPurchase) {
        customer.lastPurchase = payment.timestamp;
      }
    });
    
    // # Convert to array
    const customers = Object.values(customerPayments);
    
    // # Calculate total customers
    const totalCustomers = customers.length;
    
    // # Calculate new vs returning customers
    // # For this example, we'll consider a customer "new" if they only have one payment
    const newCustomers = customers.filter(customer => customer.payments.length === 1).length;
    const returningCustomers = totalCustomers - newCustomers;
    
    // # Calculate average spend per customer
    const totalSpend = customers.reduce((sum, customer) => sum + customer.totalSpend, 0);
    const averageSpend = totalCustomers > 0 ? totalSpend / totalCustomers : 0;
    
    // # Calculate customer lifetime value (average revenue per customer)
    const customerLifetimeValue = averageSpend;
    
    // # Calculate customers by nationality with region information
    const nationalityData = {};
    const regionData = {
      'Arab': { count: 0, totalSpend: 0, customers: new Set() },
      'Asia': { count: 0, totalSpend: 0, customers: new Set() },
      'Europe': { count: 0, totalSpend: 0, customers: new Set() },
      'Americas': { count: 0, totalSpend: 0, customers: new Set() },
      'Africa': { count: 0, totalSpend: 0, customers: new Set() },
      'Oceania': { count: 0, totalSpend: 0, customers: new Set() },
      'Unknown': { count: 0, totalSpend: 0, customers: new Set() }
    };
    
    customers.forEach(customer => {
      // # Process nationality data
      const nationality = customer.nationality || 'Unknown';
      if (!nationalityData[nationality]) {
        nationalityData[nationality] = {
          nationality,
          count: 0,
          totalSpend: 0,
          averageSpend: 0,
          customers: new Set(),
          // # Determine region from nationality
          region: getRegionFromNationality(nationality)
        };
      }
      nationalityData[nationality].count++;
      nationalityData[nationality].totalSpend += customer.totalSpend;
      nationalityData[nationality].customers.add(customer.id);
      
      // # Update region data
      const region = nationalityData[nationality].region;
      if (regionData[region]) {
        regionData[region].count++;
        regionData[region].totalSpend += customer.totalSpend;
        regionData[region].customers.add(customer.id);
      }
    });
    
    // # Calculate average spend per nationality
    Object.values(nationalityData).forEach(data => {
      data.averageSpend = data.customers.size > 0 ? data.totalSpend / data.customers.size : 0;
    });
    
    const customersByNationality = Object.values(nationalityData)
      .sort((a, b) => b.totalSpend - a.totalSpend);
    
    // # Format region data for charts
    const customersByRegion = Object.entries(regionData)
      .map(([region, data]) => ({
        region,
        count: data.count,
        totalSpend: data.totalSpend,
        customers: data.customers.size,
        averageSpend: data.customers.size > 0 ? data.totalSpend / data.customers.size : 0,
        percentage: totalCustomers > 0 ? (data.customers.size / totalCustomers) * 100 : 0
      }))
      .sort((a, b) => b.totalSpend - a.totalSpend);
    
    // # Calculate customers by product
    const productData = {};
    customers.forEach(customer => {
      customer.products.forEach(product => {
        if (!productData[product]) {
          productData[product] = {
            product,
            count: 0,
            totalSpend: 0,
            customers: new Set()
          };
        }
        productData[product].count++;
        productData[product].customers.add(customer.id);
        
        // # Calculate spend per product (approximate)
        const productPayments = customer.payments.filter(p => p.product === product);
        const productSpend = productPayments.reduce((sum, p) => sum + p.egp_total, 0);
        productData[product].totalSpend += productSpend;
      });
    });
    
    const customersByProduct = Object.values(productData)
      .map(data => ({
        product: data.product,
        count: data.count,
        customers: data.customers.size,
        totalSpend: data.totalSpend,
        averageSpend: data.customers.size > 0 ? data.totalSpend / data.customers.size : 0,
        percentage: totalCustomers > 0 ? (data.customers.size / totalCustomers) * 100 : 0
      }))
      .sort((a, b) => b.customers - a.customers);
    
    // # Calculate spending over time (monthly)
    const monthlyData = {};
    const customerAcquisition = {};
    
    payments.forEach(payment => {
      const date = payment.timestamp;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // # Initialize month data if not exists
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          totalSpend: 0,
          transactions: 0,
          uniqueCustomers: new Set()
        };
      }
      
      // # Update month data
      monthlyData[monthKey].totalSpend += payment.egp_total;
      monthlyData[monthKey].transactions++;
      monthlyData[monthKey].uniqueCustomers.add(payment.email || payment.userId || 'unknown');
      
      // # Track customer acquisition (first purchase date)
      const customerId = payment.email || payment.userId || 'unknown';
      const customer = customerPayments[customerId];
      
      if (customer && customer.firstPurchase.getTime() === date.getTime()) {
        const acquisitionMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!customerAcquisition[acquisitionMonth]) {
          customerAcquisition[acquisitionMonth] = {
            month: acquisitionMonth,
            newCustomers: 0
          };
        }
        
        customerAcquisition[acquisitionMonth].newCustomers++;
      }
    });
    
    // # Format spending over time for charts
    const spendingOverTime = Object.entries(monthlyData)
      .map(([month, data]) => ({
        month,
        totalSpend: data.totalSpend,
        transactions: data.transactions,
        uniqueCustomers: data.uniqueCustomers.size,
        averageSpend: data.uniqueCustomers.size > 0 ? data.totalSpend / data.uniqueCustomers.size : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    // # Format customer acquisition over time
    const acquisitionOverTime = Object.entries(customerAcquisition)
      .map(([month, data]) => ({
        month,
        newCustomers: data.newCustomers
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    // # Merge spending and acquisition data
    const combinedTimeData = spendingOverTime.map(spendingData => {
      const acquisitionData = acquisitionOverTime.find(a => a.month === spendingData.month);
      return {
        ...spendingData,
        newCustomers: acquisitionData ? acquisitionData.newCustomers : 0
      };
    });
    
    // # Calculate customer segments based on spending
    const customerSegments = [
      { name: 'High Value (>E£5000)', count: 0, totalSpend: 0, customers: [] },
      { name: 'Medium Value (E£2000-5000)', count: 0, totalSpend: 0, customers: [] },
      { name: 'Low Value (<E£2000)', count: 0, totalSpend: 0, customers: [] }
    ];
    
    customers.forEach(customer => {
      if (customer.totalSpend > 5000) {
        customerSegments[0].count++;
        customerSegments[0].totalSpend += customer.totalSpend;
        customerSegments[0].customers.push(customer);
      } else if (customer.totalSpend >= 2000) {
        customerSegments[1].count++;
        customerSegments[1].totalSpend += customer.totalSpend;
        customerSegments[1].customers.push(customer);
      } else {
        customerSegments[2].count++;
        customerSegments[2].totalSpend += customer.totalSpend;
        customerSegments[2].customers.push(customer);
      }
    });
    
    // # Calculate retention rate (returning customers / total customers)
    const retentionRate = totalCustomers > 0 ? (returningCustomers / totalCustomers) * 100 : 0;
    
    // # Calculate customer acquisition cost (placeholder - would need more data)
    const customerAcquisitionCost = 0;
    
    // # Calculate customer growth rate
    let customerGrowthRate = 0;
    if (combinedTimeData.length > 1) {
      const firstMonth = combinedTimeData[0].uniqueCustomers;
      const lastMonth = combinedTimeData[combinedTimeData.length - 1].uniqueCustomers;
      
      if (firstMonth > 0) {
        customerGrowthRate = ((lastMonth - firstMonth) / firstMonth) * 100;
      }
    }
    
    // # Format customer details for table view
    const customerDetails = customers.map(customer => ({
      id: customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      nationality: customer.nationality,
      region: getRegionFromNationality(customer.nationality),
      totalSpend: customer.totalSpend,
      purchaseCount: customer.payments.length,
      firstPurchase: customer.firstPurchase,
      lastPurchase: customer.lastPurchase,
      products: Array.from(customer.products),
      mentors: Array.from(customer.mentors)
    })).sort((a, b) => b.totalSpend - a.totalSpend);
    
    // # Update customerData with processed data
    customerData.totalCustomers = totalCustomers;
    customerData.newCustomers = newCustomers;
    customerData.returningCustomers = returningCustomers;
    customerData.averageSpend = averageSpend;
    customerData.customerLifetimeValue = customerLifetimeValue;
    customerData.customersByNationality = customersByNationality;
    customerData.customersByRegion = customersByRegion;
    customerData.customersByProduct = customersByProduct;
    customerData.spendingOverTime = combinedTimeData;
    customerData.customerSegments = customerSegments;
    customerData.retentionRate = retentionRate;
    customerData.customerAcquisitionCost = customerAcquisitionCost;
    customerData.customerDetails = customerDetails;
    customerData.customerGrowthRate = customerGrowthRate;
    
    // # Set the state with the processed data
    setCustomerData(customerData);
    
    return customerData;
  };
  
  // # Helper function to determine region from nationality
  const getRegionFromNationality = (nationality) => {
    // # Handle empty or undefined nationality
    if (!nationality || nationality === 'Unknown') {
      return 'Unknown';
    }
    
    // # Normalize the nationality string
    const normalizedNationality = nationality.toLowerCase().trim();
    
    // # Direct mapping for common countries
    const directMapping = {
      'egypt': 'Arab',
      'egyptian': 'Arab',
      'saudi': 'Arab',
      'saudi arabia': 'Arab',
      'uae': 'Arab',
      'emirates': 'Arab',
      'kuwait': 'Arab',
      'qatar': 'Arab',
      'bahrain': 'Arab',
      'oman': 'Arab',
      'jordan': 'Arab',
      'lebanon': 'Arab',
      'iraq': 'Arab',
      'syria': 'Arab',
      'palestine': 'Arab',
      'yemen': 'Arab',
      'libya': 'Arab',
      'algeria': 'Arab',
      'tunisia': 'Arab',
      'morocco': 'Arab',
      'sudan': 'Arab',
      'usa': 'Americas',
      'united states': 'Americas',
      'america': 'Americas',
      'canada': 'Americas',
      'mexico': 'Americas',
      'brazil': 'Americas',
      'uk': 'Europe',
      'united kingdom': 'Europe',
      'england': 'Europe',
      'france': 'Europe',
      'germany': 'Europe',
      'italy': 'Europe',
      'spain': 'Europe',
      'russia': 'Europe',
      'china': 'Asia',
      'japan': 'Asia',
      'india': 'Asia',
      'pakistan': 'Asia',
      'australia': 'Oceania',
      'new zealand': 'Oceania'
    };
    
    // # Check for direct matches or partial matches
    for (const [key, region] of Object.entries(directMapping)) {
      if (normalizedNationality === key || normalizedNationality.includes(key)) {
        return region;
      }
    }
    
    // # Check if it's in the Arab countries
    for (const [key, data] of Object.entries(nationalityData)) {
      if (data.searchKeys.includes('arab') && 
          (data.name.toLowerCase() === normalizedNationality || 
           data.searchKeys.some(k => normalizedNationality.includes(k)))) {
        return 'Arab';
      }
    }
    
    // # Check other regions
    const regions = ['Asia', 'Europe', 'Americas', 'Africa', 'Oceania'];
    for (const region of regions) {
      for (const [key, data] of Object.entries(nationalityData)) {
        if (data.searchKeys.includes(region.toLowerCase()) && 
            (data.name.toLowerCase() === normalizedNationality || 
             data.searchKeys.some(k => normalizedNationality.includes(k)))) {
          return region;
        }
      }
    }
    
    return 'Unknown';
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${value.toLocaleString()}`;
  };
  
  // # Format percentage values
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };
  
  // # Format month labels
  const formatMonth = (monthKey) => {
    if (!monthKey || typeof monthKey !== 'string') return '';
    
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  return (
    <div className="customer_analysis_container">
      {error && (
        <div className="customer_analysis_error">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="customer_analysis_loading">
          Loading customer data...
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="customer_analysis_overview">
            <div className="customer_analysis_stats-grid">
              <div className="customer_analysis_stat-card">
                <FaUsers className="customer_analysis_stat-icon" />
                <h3>Total Customers</h3>
                <p className="customer_analysis_stat-value">
                  {customerData.totalCustomers.toLocaleString()}
                </p>
              </div>
              <div className="customer_analysis_stat-card">
                <FaUserPlus className="customer_analysis_stat-icon" />
                <h3>New Customers</h3>
                <p className="customer_analysis_stat-value">
                  {customerData.newCustomers.toLocaleString()} 
                  <span className="customer_analysis_stat-percentage">
                    ({customerData.totalCustomers > 0 ? ((customerData.newCustomers / customerData.totalCustomers) * 100).toFixed(1) : 0}%)
                  </span>
                </p>
              </div>
              <div className="customer_analysis_stat-card">
                <FaUserCheck className="customer_analysis_stat-icon" />
                <h3>Returning Customers</h3>
                <p className="customer_analysis_stat-value">
                  {customerData.returningCustomers.toLocaleString()}
                  <span className="customer_analysis_stat-percentage">
                    ({customerData.totalCustomers > 0 ? ((customerData.returningCustomers / customerData.totalCustomers) * 100).toFixed(1) : 0}%)
                  </span>
                </p>
              </div>
              <div className="customer_analysis_stat-card">
                <FaMoneyBillWave className="customer_analysis_stat-icon" />
                <h3>Average Spend</h3>
                <p className="customer_analysis_stat-value">
                  {formatCurrency(customerData.averageSpend)}
                </p>
              </div>
              <div className="customer_analysis_stat-card">
                <FaShoppingCart className="customer_analysis_stat-icon" />
                <h3>Retention Rate</h3>
                <p className="customer_analysis_stat-value">
                  {formatPercentage(customerData.retentionRate)}
                </p>
              </div>
              <div className="customer_analysis_stat-card">
                <div className={`customer_analysis_stat-icon ${customerData.customerGrowthRate >= 0 ? 'positive' : 'negative'}`}>
                  {customerData.customerGrowthRate >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                </div>
                <h3>Customer Growth</h3>
                <p className={`customer_analysis_stat-value ${customerData.customerGrowthRate >= 0 ? 'positive' : 'negative'}`}>
                  {customerData.customerGrowthRate >= 0 ? '+' : ''}{customerData.customerGrowthRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          
          {/* Customer Growth Trend */}
          <div className="customer_analysis_chart-container">
            <h3>Customer Growth Over Time</h3>
            <div className="customer_analysis_chart-grid">
              <div className="customer_analysis_chart">
                <h4>Monthly Spending & New Customers</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={customerData.spendingOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={formatMonth}
                    />
                    <YAxis 
                      yAxisId="left"
                      tickFormatter={(value) => `E£${value.toLocaleString()}`}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'totalSpend') return formatCurrency(value);
                        return value;
                      }}
                      labelFormatter={formatMonth}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="totalSpend" 
                      fill="#8884d8" 
                      name="Total Spend (EGP)" 
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="uniqueCustomers" 
                      stroke="#82ca9d" 
                      name="Unique Customers" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="newCustomers" 
                      stroke="#ffc658" 
                      name="New Customers" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              
              <div className="customer_analysis_chart">
                <h4>Average Spend Per Customer</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={customerData.spendingOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={formatMonth}
                    />
                    <YAxis 
                      tickFormatter={(value) => `E£${value.toLocaleString()}`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={formatMonth}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="averageSpend" 
                      stroke="#8884d8" 
                      name="Avg. Spend per Customer" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Customer Segments */}
          <div className="customer_analysis_chart-container">
            <h3>Customer Segments</h3>
            <div className="customer_analysis_chart-grid">
              <div className="customer_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerData.customerSegments}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      label
                    >
                      {customerData.customerSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="customer_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={customerData.customerSegments}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={100}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'totalSpend') return formatCurrency(value);
                        return value;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" name="Number of Customers" />
                    <Bar dataKey="totalSpend" fill="#82ca9d" name="Total Spend (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Nationality Distribution Section with Tabs */}
          <div className="customer_analysis_chart-container">
            <div className="customer_analysis_chart-header">
              <h3>Customer Distribution</h3>
              <div className="customer_analysis_tabs">
                <button 
                  className={`customer_analysis_tab ${nationalityView === 'country' ? 'active' : ''}`}
                  onClick={() => setNationalityView('country')}
                >
                  By Country
                </button>
                <button 
                  className={`customer_analysis_tab ${nationalityView === 'region' ? 'active' : ''}`}
                  onClick={() => setNationalityView('region')}
                >
                  By Region
                </button>
              </div>
            </div>
            
            <div className="customer_analysis_chart-grid">
              {/* Pie Chart */}
              <div className="customer_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={nationalityView === 'country' 
                        ? customerData.customersByNationality 
                        : customerData.customersByRegion}
                      dataKey="count"
                      nameKey={nationalityView === 'country' ? 'nationality' : 'region'}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    >
                      {(nationalityView === 'country' 
                        ? customerData.customersByNationality 
                        : customerData.customersByRegion).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Bar Chart */}
              <div className="customer_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={nationalityView === 'country' 
                      ? customerData.customersByNationality.slice(0, 10) // Show top 10 countries
                      : customerData.customersByRegion}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={nationalityView === 'country' ? 'nationality' : 'region'} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'totalSpend') return formatCurrency(value);
                        return value;
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Number of Customers" />
                    <Bar yAxisId="right" dataKey="totalSpend" fill="#82ca9d" name="Total Spend (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Show note for country view if there are many countries */}
            {nationalityView === 'country' && customerData.customersByNationality.length > 10 && (
              <div className="customer_analysis_note">
                * Showing top 10 countries by customer count. {customerData.customersByNationality.length - 10} more countries not shown in the bar chart.
              </div>
            )}
          </div>
          
          {/* Customers by Product */}
          <div className="customer_analysis_chart-container">
            <h3>Customers by Product</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={customerData.customersByProduct}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'totalSpend') return formatCurrency(value);
                    return value;
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Number of Customers" />
                <Bar yAxisId="right" dataKey="totalSpend" fill="#82ca9d" name="Total Spend (EGP)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Customer Scatter Plot */}
          <div className="customer_analysis_chart-container">
            <h3>Customer Purchase Behavior</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="purchaseCount" 
                  name="Number of Purchases" 
                  type="number"
                />
                <YAxis 
                  dataKey="totalSpend" 
                  name="Total Spend (EGP)" 
                  tickFormatter={(value) => `E£${value.toLocaleString()}`}
                />
                <ZAxis 
                  dataKey="productCount" 
                  name="Number of Products" 
                  range={[50, 500]} 
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Total Spend (EGP)') return formatCurrency(value);
                    return value;
                  }}
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="customer_analysis_tooltip">
                          <p className="customer_analysis_tooltip-name">{data.name || data.email || 'Unknown'}</p>
                          <p>Purchases: {data.purchaseCount}</p>
                          <p>Total Spend: {formatCurrency(data.totalSpend)}</p>
                          <p>Products: {data.productCount}</p>
                          <p>Nationality: {data.nationality || 'Unknown'}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Scatter 
                  name="Customers" 
                  data={customerData.customerDetails} 
                  fill="#8884d8" 
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          
          {/* Customer Details Table */}
          <div className="customer_analysis_table-container">
            <div className="customer_analysis_table-header">
              <h3>Customer Details</h3>
              <div className="customer_analysis_search-container">
                <input
                  type="text"
                  className="customer_analysis_search-input"
                  placeholder="Search by name, email, nationality..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="customer_analysis_table-wrapper">
              <table className="customer_analysis_table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Nationality</th>
                    <th>Total Spend</th>
                    <th>Purchases</th>
                    <th>First Purchase</th>
                    <th>Last Purchase</th>
                    <th>Products</th>
                  </tr>
                </thead>
                <tbody>
                  {customerData.customerDetails
                    .filter(customer => {
                      if (!searchTerm) return true;
                      const searchLower = searchTerm.toLowerCase();
                      return (
                        (customer.name || '').toLowerCase().includes(searchLower) ||
                        (customer.email || '').toLowerCase().includes(searchLower) ||
                        (customer.nationality || '').toLowerCase().includes(searchLower) ||
                        (customer.products || []).some(product => 
                          product.toLowerCase().includes(searchLower)
                        )
                      );
                    })
                    .map((customer, index) => (
                      <tr key={index}>
                        <td title={customer.name || 'Unknown'}>{customer.name || 'Unknown'}</td>
                        <td title={customer.email || 'Unknown'}>{customer.email || 'Unknown'}</td>
                        <td title={customer.nationality || 'Unknown'}>{customer.nationality || 'Unknown'}</td>
                        <td title={formatCurrency(customer.totalSpend)}>{formatCurrency(customer.totalSpend)}</td>
                        <td title={customer.purchaseCount}>{customer.purchaseCount}</td>
                        <td title={customer.firstPurchase.toLocaleDateString()}>{customer.firstPurchase.toLocaleDateString()}</td>
                        <td title={customer.lastPurchase.toLocaleDateString()}>{customer.lastPurchase.toLocaleDateString()}</td>
                        <td title={customer.products.join(', ')}>{customer.products.join(', ')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerAnalysis;
